import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { ComponentTable } from "@components/Tables";
import { showWarningToast } from "@configs/react-toastify/snackbar";
import { VEX_SNAPSHOT_SOURCE } from "@constants/vex";
import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useEscapeKey from "@hooks/useEscapeKey";

const DocumentInfoModal = ({
  isOpen,
  onClose,
  verification = null,
  components = [],
  licensePolicyData = [],
  loading = false,
  productSeq = null,
  editableFrozenVexSnapshot = false,
  "data-testid": dataTestId = "document-info-modal",
}) => {
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();

  const hasViewPermission = verification?.hasViewPermission ?? verification?.["hasViewPermission"];

  const handleViewDetails = () => {
    if (!hasViewPermission) {
      showWarningToast(t("product.verification-version-no-view-permission"), "");
      return;
    }

    if (!verification || !verification.projectSeq || !verification.verificationSeq) {
      console.error("Missing verification data for navigation");
      return;
    }

    const { projectSeq, verificationSeq, verificationType } = verification;

    switch (verificationType) {
      case 'FossId':
        navigate(`/projects/${projectSeq}/fossid/${verificationSeq}`);
        break;
      case 'Clarity':
        navigate(`/projects/${projectSeq}/clarity/${verificationSeq}`);
        break;
      case 'SBOM':
        navigate(`/projects/${projectSeq}/sbom/${verificationSeq}`);
        break;
      case 'ClarityAir':
          navigate(`/projects/${projectSeq}/clarity-air/${verificationSeq}`);        
        break;
      default:
        console.error(`Unknown verification type: ${verificationType}`);
        break;
    }

    onClose();
  };

  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex justify-start xl:justify-center items-center backdrop-blur-[1px] overflow-x-auto p-4`} data-testid={`${dataTestId}-overlay`}>
      <div className={`${colors.bgModal} max-h-[90vh] w-[95vw] min-w-[1216px] max-w-[1400px] flex-shrink-0 rounded-lg shadow-xl flex flex-col`} data-testid={dataTestId}>
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>
              {t("product.components-by-verification")}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col min-h-0 overflow-hidden px-6 pb-4" data-testid={`${dataTestId}-content`}>
          {loading ? (
            <div className={`flex items-center justify-center h-64 ${colors.bgSecondary} rounded-lg`} data-testid={fieldValueTestId("loading-message")}>
              <div className="flex items-center space-x-2">
                <div className={`h-4 w-4 animate-spin rounded-full border-b-2 ${colors.borderLoader}`}></div>
                <p className={`text-sm ${colors.textSecondary}`}>
                  {t("common.loading")}
                </p>
              </div>
            </div>
          ) : components.length === 0 ? (
            <div className={`flex items-center justify-center h-64 ${colors.bgSecondary} rounded-lg`}>
              <p className={`text-sm ${colors.textSecondary}`} data-testid={fieldValueTestId("no-components-message")}>
                {t("product.no-components-found")}
              </p>
            </div>
          ) : (
            <ComponentTable
              data={components}
              licensePolicyData={licensePolicyData}
              seq="mergedProductComponentSeq"
              className="p-0"
              scrollable={true}
              validationMode="export"
              showShieldFilter
              editableFrozenVexSnapshot={editableFrozenVexSnapshot}
              frozenVexSnapshot={
                productSeq != null && verification
                  ? {
                      source: VEX_SNAPSHOT_SOURCE.PRODUCT,
                      productSeq,
                      projectId: verification.projectId,
                      verificationVersion: verification.verificationVersion,
                      verificationType: verification.verificationType,
                    }
                  : null
              }
            />
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 ${colors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
          <div className="flex items-center justify-end space-x-3">
            <OutlineButton data-testid={`${dataTestId}-close-button`} onClick={onClose}>
              {t("common.close-button")}
            </OutlineButton>
            <PrimaryButton data-testid={`${dataTestId}-confirm-button`} onClick={handleViewDetails}>
              {t("product.view-details")}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentInfoModal;
